/** Dates and relative times in the reader's language (react-intl's locale), for pages that moved to the catalogs:
 * the same shapes as lib/format.ts's `date()` and `relative()`, which format in the browser's language and English. */
import { useMemo } from "react";
import { useIntl } from "react-intl";

type When = string | null | undefined;

export interface Dates {
  /** "12 Oct 2026" (empty value: "—"). */
  date: (value: When) => string;
  /** "12 Oct 2026, 14:05". */
  dateTime: (value: When) => string;
  /** "3 minutes ago", "yesterday"; older than 30 days: the date; null when there is no value. */
  relative: (value: When) => string | null;
  /** "in 4 minutes" for a moment to come (past or now: "now"). */
  until: (value: string) => string;
}

export function useDates(): Dates {
  const intl = useIntl();
  return useMemo(() => {
    const date = (value: When) => (value ? intl.formatDate(value, { year: "numeric", month: "short", day: "numeric" }) : "—");
    const dateTime = (value: When) =>
      value ? intl.formatDate(value, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
    const ago = (seconds: number) => {
      const s = Math.abs(seconds);
      const sign = seconds < 0 ? -1 : 1;
      if (s < 60) return intl.formatRelativeTime(0, "second", { numeric: "auto" });
      if (s < 3600) return intl.formatRelativeTime(sign * Math.max(1, Math.round(s / 60)), "minute", { numeric: "auto" });
      if (s < 86400) return intl.formatRelativeTime(sign * Math.round(s / 3600), "hour", { numeric: "auto" });
      return intl.formatRelativeTime(sign * Math.round(s / 86400), "day", { numeric: "auto" });
    };
    const relative = (value: When) => {
      if (!value) return null;
      const seconds = (new Date(value).getTime() - Date.now()) / 1000;
      return seconds < -86400 * 30 ? date(value) : ago(Math.min(seconds, 0));
    };
    const until = (value: string) => ago(Math.max((new Date(value).getTime() - Date.now()) / 1000, 0));
    return { date, dateTime, relative, until };
  }, [intl]);
}
